import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Badge,
  Skeleton,
  Alert,
  Modal,
  Checkbox,
  Select,
  TextArea,
  ConfirmDialog,
  EmptyState,
} from '@bbpos/ui';
import { billingService } from '@/services/billing.service';
import type { BillWithItems } from '@/services/billing.service';
import { usePrinterStore } from '@/stores/use-printer-store';
import { useTenantStore } from '@/stores/use-tenant-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useLocationStore } from '@/stores/use-location-store';
import { useShiftStore } from '@/stores/use-shift-store';
import { useRegisterStore } from '@/stores/use-register-store';
import { ApiError } from '@/lib/api-client';
import { ManagerApprovalDialog } from '@/components/common/ManagerApprovalDialog';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { getLocationTimezone } from '@/lib/tenant-clock';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { STATUS_BADGE_MAP, formatBillNumber, isDraftBillNumber } from '@/lib/bill-constants';
import { buildReceiptData } from '@/lib/receipt-data';
import { ReturnTrackingCard } from '@/components/features/returns/ReturnTrackingCard';
import { useReturnUpdates } from '@/hooks/use-return-updates';
import type { BillReturn } from '@/services/billing.service';
import { NOT_GST_REGISTERED } from '@/lib/gst';

const RETURN_MODE_OPTIONS = [
  { value: 'CASH', label: 'Cash Refund' },
  { value: 'STORE_CREDIT', label: 'Store Credit' },
  { value: 'EXCHANGE', label: 'Exchange' },
  { value: 'PAYMENT_REVERSAL', label: 'Payment Reversal' },
];

/** Refund modes that pay money out of the drawer — the till's shift gate applies. */
const DRAWER_RETURN_MODES = new Set(['CASH', 'PAYMENT_REVERSAL']);

const RETURN_REASON_OPTIONS = [
  { value: 'DEFECTIVE', label: 'Defective' },
  { value: 'CHANGED_MIND', label: 'Changed Mind' },
  { value: 'WRONG_ITEM', label: 'Wrong Item' },
  { value: 'SIZE_MISMATCH', label: 'Size Mismatch' },
  { value: 'OTHER', label: 'Other' },
];

// ─── Icons ──────────────────────────────────────────────────────────────────

/** Units of a line that can still be returned: sold − returned − held by pending approvals (T02). */
function returnableQty(item: BillWithItems['items'][number]): number {
  return Math.max(0, Number(item.quantity) - Number(item.returned_qty ?? 0) - Number(item.pending_return_qty ?? 0));
}

function ChevronLeftIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function PrinterIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function RotateCcwIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
    </svg>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function BillDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  // Share the invoice by WhatsApp or email (POST /communications/send-invoice).
  const [showShare, setShowShare] = useState(false);
  const [shareChannel, setShareChannel] = useState<'WHATSAPP' | 'EMAIL'>('WHATSAPP');
  const [shareRecipient, setShareRecipient] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const tax = useTaxRegime();

  const [bill, setBill] = useState<BillWithItems | null>(null);
  // gst-parked 090 (D5): billed while not GST registered — no tax shown.
  const noGst = tax.regime === 'GST_INDIA' && bill?.supply_type === 'NONE';
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Return modal state
  const [showReturn, setShowReturn] = useState(false);
  const [selectedReturnItems, setSelectedReturnItems] = useState<Set<string>>(new Set());
  // Units to return per bill item. Defaults to the full line quantity when an
  // item is first selected; the cashier can lower it for a partial return.
  const [returnQuantities, setReturnQuantities] = useState<Record<string, number>>({});
  const [returnMode, setReturnMode] = useState('CASH');
  const [returnReason, setReturnReason] = useState('DEFECTIVE');
  const [returnNotes, setReturnNotes] = useState('');
  const [isProcessingReturn, setIsProcessingReturn] = useState(false);
  // One X-Idempotency-Key per return attempt (T02): a resend of the same
  // attempt is replayed by the API instead of filing a second request.
  const [returnAttemptKey, setReturnAttemptKey] = useState('');
  // T16: set to the server's reason while the manager-PIN dialog is open.
  const [returnApprovalReason, setReturnApprovalReason] = useState<string | null>(null);
  // T03: the return whose cancel request is in flight.
  const [cancellingReturnId, setCancellingReturnId] = useState<string | null>(null);

  // Void confirm dialog
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);

  // Download loading states
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printSuccess, setPrintSuccess] = useState<string | null>(null);

  // Receipt dropdown
  const [showReceiptMenu, setShowReceiptMenu] = useState(false);

  // Printer store
  const printerStatus = usePrinterStore((s) => s.connectionStatus);
  const printerType = usePrinterStore((s) => s.connectionType);
  const printReceipt = usePrinterStore((s) => s.printReceipt);
  const printDuplicate = usePrinterStore((s) => s.printDuplicate);
  const tenant = useTenantStore((s) => s.tenant);
  const currentLocation = useLocationStore((s) => s.currentLocation);
  // Tenant-configurable Return policy (see Admin → Settings → Return policy).
  // When false, the Process Return CTA is hidden — the backend will also
  // reject the same call with 403, but hiding keeps the UX honest.
  const canIssueCreditNote = useAuthStore(
    (s) => s.profile?.permissions?.can_issue_credit_note ?? false,
  );
  const userId = useAuthStore((s) => s.profile?.id);
  const userRole = useAuthStore((s) => s.profile?.role);

  // Close receipt dropdown on Escape + keyboard shortcut p to print
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showReceiptMenu) setShowReceiptMenu(false);
      // p shortcut for print receipt (not when in input/textarea)
      if (e.key === 'p' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
          e.preventDefault();
          handleThermalPrint();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showReceiptMenu, bill, printerStatus, printerType]);

  // ── Fetch bill ───────────────────────────────────────────────────────────
  // `silent`: refetch in place (a live return update) without the skeleton.
  const fetchBill = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const data = await billingService.getBill(id);
      setBill(data as BillWithItems);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load bill details.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchBill(); }, [fetchBill]);

  // T03: a manager approved / rejected a return on this bill — show it now.
  useReturnUpdates(() => { fetchBill(true); }, id);

  // ── Download helpers ─────────────────────────────────────────────────────
  const downloadBlob = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    // Defer revocation to ensure the browser has started the download
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, []);

  const handlePrintReceipt = useCallback(async (format: '80mm' | '58mm' = '80mm') => {
    if (!id || !bill) return;
    setIsDownloading(true);
    try {
      const blob = await billingService.getReceipt(id, format);
      downloadBlob(blob, `${bill.bill_number}-receipt-${format}.pdf`);
    } catch {
      setError('Failed to download receipt.');
    } finally {
      setIsDownloading(false);
    }
    setShowReceiptMenu(false);
  }, [id, bill, downloadBlob]);

  /** Build receipt data from the loaded bill + tenant/location stores. */
  const getReceiptData = useCallback(
    () => buildReceiptData(bill, tenant, currentLocation),
    [bill, tenant, currentLocation],
  );

  /** Print receipt via thermal printer (ESC/POS) or fall back to PDF download. */
  const handleThermalPrint = useCallback(() => {
    if (!bill) return;
    const isPrinterReady = printerStatus === 'connected' && printerType !== 'browser';
    const data = getReceiptData();

    if (isPrinterReady && data) {
      setIsPrinting(true);
      try {
        printReceipt(data.billData, data.tenantData, data.locationData, {
          taxName: tax.taxName,
          taxIdLabel: tax.taxIdLabel,
          splitLabels: tax.splitLabels,
          showHsn: tax.showHsn,
        });
        setPrintSuccess('Receipt sent to printer');
        setTimeout(() => setPrintSuccess(null), 3000);
      } catch {
        setError('Failed to print receipt.');
      } finally {
        setIsPrinting(false);
      }
    } else {
      // Fallback to PDF download
      handlePrintReceipt('80mm');
    }
  }, [bill, printerStatus, printerType, getReceiptData, printReceipt, handlePrintReceipt, tax]);

  /** Print a duplicate receipt. */
  const handleDuplicatePrint = useCallback(() => {
    if (!bill) return;
    const isPrinterReady = printerStatus === 'connected' && printerType !== 'browser';
    const data = getReceiptData();

    if (isPrinterReady && data) {
      setIsPrinting(true);
      try {
        printDuplicate(data.billData, data.tenantData, data.locationData, {
          taxName: tax.taxName,
          taxIdLabel: tax.taxIdLabel,
          splitLabels: tax.splitLabels,
          showHsn: tax.showHsn,
        });
        setPrintSuccess('Duplicate receipt sent to printer');
        setTimeout(() => setPrintSuccess(null), 3000);
      } catch {
        setError('Failed to print duplicate.');
      } finally {
        setIsPrinting(false);
      }
    } else {
      handlePrintReceipt('80mm');
    }
  }, [bill, printerStatus, printerType, getReceiptData, printDuplicate, handlePrintReceipt, tax]);

  const handleDownloadInvoice = useCallback(async () => {
    if (!id || !bill) return;
    setIsDownloading(true);
    try {
      const blob = await billingService.getInvoice(id);
      downloadBlob(blob, `${bill.bill_number}-invoice.pdf`);
    } catch {
      setError('Failed to download invoice.');
    } finally {
      setIsDownloading(false);
    }
  }, [id, bill, downloadBlob]);

  // T04: on a return bill the credit note is the bill itself; on a fully
  // returned original it is the latest credit note issued against it.
  const creditNoteId = bill?.return_against
    ? bill.id
    : bill?.credit_notes?.[bill.credit_notes.length - 1]?.id;

  const handleDownloadCreditNote = useCallback(async () => {
    if (!creditNoteId || !bill) return;
    setIsDownloading(true);
    try {
      const blob = await billingService.getCreditNote(creditNoteId);
      downloadBlob(blob, `${bill.bill_number}-credit-note.pdf`);
    } catch {
      setError('Failed to download credit note.');
    } finally {
      setIsDownloading(false);
    }
  }, [creditNoteId, bill, downloadBlob]);

  // ── Return flow ──────────────────────────────────────────────────────────
  const openReturnModal = useCallback(() => {
    setSelectedReturnItems(new Set());
    setReturnMode('CASH');
    setReturnReason('DEFECTIVE');
    setReturnNotes('');
    setReturnAttemptKey(crypto.randomUUID());
    setShowReturn(true);
  }, []);

  const toggleReturnItem = useCallback((itemId: string, fullQty = 1) => {
    setSelectedReturnItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
    setReturnQuantities((prev) => {
      const next = { ...prev };
      if (next[itemId] !== undefined) delete next[itemId];
      else next[itemId] = fullQty;
      return next;
    });
  }, []);

  /** Clamp the per-item return quantity to 1..fullQty. */
  const setReturnQuantity = useCallback((itemId: string, qty: number, fullQty: number) => {
    const clamped = Math.max(1, Math.min(fullQty, Math.floor(qty) || 1));
    setReturnQuantities((prev) => ({ ...prev, [itemId]: clamped }));
  }, []);

  /**
   * T16: the first attempt sets `on_device_approval`, so a return above the
   * approval limit comes back as APPROVAL_REQUIRED and the manager can approve
   * here with their PIN (retry with `approval_token`). Without a manager the
   * cashier retries with neither, which sends the request to a manager.
   */
  const submitReturn = useCallback(async (approval: { approval_token?: string; on_device_approval?: boolean }) => {
    if (!id || selectedReturnItems.size === 0) return;
    setIsProcessingReturn(true);
    try {
      const result = await billingService.processReturn(id, {
        items: Array.from(selectedReturnItems).map((itemId) => ({
          item_id: itemId,
          quantity: returnQuantities[itemId] ?? 1,
        })),
        return_mode: returnMode,
        reason: returnReason,
        reason_notes: returnNotes || undefined,
        ...approval,
      }, returnAttemptKey || undefined);
      setReturnApprovalReason(null);
      setShowReturn(false);
      if ('request_id' in result) {
        // Above the tenant's approval threshold: nothing has been refunded yet.
        // The bill stays COMPLETED until a manager approves the request.
        const label = result.return_number ? `Return ${result.return_number}` : 'This return';
        setSuccessMsg(
          `Awaiting approval: ${label} is above the approval limit. A manager has been notified and the refund will be issued once it is approved.`,
        );
      } else {
        const returned = result.return?.return_number
          ? `Return ${result.return.return_number} completed.`
          : 'Return processed successfully.';
        // CU2-003: the part paid with points comes back as points; only the
        // rest is refunded by the chosen mode.
        const pointsBack = result.credit_note?.points_back ?? 0;
        const done = pointsBack > 0
          ? `${returned} ${pointsBack} points back to the customer; ${formatCurrency(result.credit_note.money_refund ?? 0, currency)} refunded.`
          : returned;
        // T04: a store credit's code is what the customer redeems — keep it
        // on screen until dismissed so it can be read out or written down.
        const code = result.credit_note?.redemption_code;
        if (code) {
          setSuccessMsg(`${done} Store credit code: ${code}`);
        } else {
          setSuccessMsg(done);
          setTimeout(() => setSuccessMsg(null), 4000);
        }
      }
      fetchBill();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'APPROVAL_REQUIRED') {
        setReturnApprovalReason(err.message);
      } else if (err instanceof ApiError) {
        setReturnApprovalReason(null);
        setError(err.message);
      } else {
        setReturnApprovalReason(null);
        setError('Failed to process return.');
      }
    } finally {
      setIsProcessingReturn(false);
    }
  }, [id, selectedReturnItems, returnQuantities, returnMode, returnReason, returnNotes, returnAttemptKey, fetchBill, currency]);

  // A cash refund leaves the drawer, which belongs to a shift (and, where the
  // store has registers, an open register) — the same gate as a sale on the
  // billing screen, so the refund lands in that shift's cash closing.
  const ensureActiveShift = useShiftStore((s) => s.ensureActiveShift);
  const ensureRegisterReady = useRegisterStore((s) => s.ensureReady);
  const handleProcessReturn = useCallback(async () => {
    if (DRAWER_RETURN_MODES.has(returnMode)) {
      if (!(await ensureActiveShift())) {
        navigate('/pos/shift/open');
        return;
      }
      const locationId = useLocationStore.getState().currentLocationId;
      if (locationId) {
        const readiness = await ensureRegisterReady(locationId);
        if (readiness !== 'ready' && readiness !== 'no-registers') {
          navigate('/pos/register');
          return;
        }
      }
    }
    await submitReturn({ on_device_approval: true });
  }, [returnMode, ensureActiveShift, ensureRegisterReady, navigate, submitReturn]);

  /** T03: a cashier withdraws their own waiting return; a manager any of them. */
  const canCancelReturn = useCallback(
    (r: BillReturn) =>
      r.requested_by === userId || userRole === 'MANAGER' || userRole === 'OWNER',
    [userId, userRole],
  );

  const handleCancelReturn = useCallback(async (r: BillReturn) => {
    setCancellingReturnId(r.id);
    try {
      await billingService.cancelReturn(r.id);
      setSuccessMsg(`Return ${r.return_number} cancelled. Its items can be returned again.`);
      setTimeout(() => setSuccessMsg(null), 4000);
      fetchBill(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to cancel the return.');
    } finally {
      setCancellingReturnId(null);
    }
  }, [fetchBill]);

  // ── Void flow ────────────────────────────────────────────────────────────
  const handleVoidBill = useCallback(async () => {
    if (!id) return;
    setIsVoiding(true);
    try {
      await billingService.voidBill(id, voidReason);
      setShowVoidConfirm(false);
      setVoidReason('');
      setSuccessMsg('Bill voided successfully.');
      setTimeout(() => setSuccessMsg(null), 4000);
      fetchBill();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to void bill.');
      }
    } finally {
      setIsVoiding(false);
    }
  }, [id, fetchBill]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
        <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-white dark:bg-neutral-800 shadow-xs">
          <Skeleton variant="text" width={200} height={20} />
        </header>
        <div className="p-6 space-y-4">
          <Skeleton variant="rect" width="100%" height={120} />
          <Skeleton variant="table-row" count={5} />
          <Skeleton variant="rect" width="100%" height={80} />
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
        <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-white dark:bg-neutral-800 shadow-xs">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos/bills')}>
            <ChevronLeftIcon />
            <span className="ml-1">Bills</span>
          </Button>
        </header>
        <EmptyState
          variant="page"
          title="Bill not found"
          description={error ?? 'The requested bill could not be loaded.'}
          action={{ label: 'Back to History', onClick: () => navigate('/pos/bills') }}
        />
      </div>
    );
  }

  const isSplit = bill.payment_method === 'SPLIT' && bill.payments.length > 0;

  return (
    <div className="flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
      {/* ═══ Header ═══ */}
      <header className="shrink-0 flex items-center gap-4 px-6 h-14 bg-white dark:bg-neutral-800 shadow-xs dark:shadow-none">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pos/bills')}>
          <ChevronLeftIcon />
          <span className="ml-1 hidden sm:inline">Bills</span>
        </Button>
        <div className="flex items-center gap-3">
          <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            {formatBillNumber(bill)}
          </h1>
          <Badge variant={STATUS_BADGE_MAP[bill.status]} size="sm" dot>
            {bill.status}
          </Badge>
          {/* T19: sold offline — the receipt the customer holds carries this number. */}
          {bill.offline_bill_number && (
            <span className="text-xs font-mono text-neutral-500 dark:text-neutral-400">
              Offline ref {bill.offline_bill_number}
            </span>
          )}
          {bill.offline_payment_verification === 'UNVERIFIED' && (
            <Badge variant="warning" size="sm">Paid offline — unverified</Badge>
          )}
        </div>
        <span className="ml-auto text-xs text-neutral-500 dark:text-neutral-400">
          {bill.created_by_name ? `${bill.created_by_name} · ` : ''}
          {formatDateTime(bill.created_at, getLocationTimezone(bill.location_id))}
        </span>
      </header>

      {/* ═══ Banners ═══ */}
      {error && (
        <div className="px-6 pt-3 shrink-0">
          <Alert variant="danger" description={error} dismissible onDismiss={() => setError(null)} />
        </div>
      )}
      {successMsg && (
        <div className="px-6 pt-3 shrink-0">
          <Alert variant="success" description={successMsg} dismissible onDismiss={() => setSuccessMsg(null)} />
        </div>
      )}

      {/* ═══ Content ═══ */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">

        {/* ── Return link ────────────────────────────────────────────── */}
        {bill.return_against && (
          <div className="bg-warning-50 dark:bg-warning-950 border border-warning-300 dark:border-warning-700 rounded-lg px-4 py-3 text-sm text-warning-800 dark:text-warning-200">
            This is a credit note for bill{' '}
            <Link
              to={`/pos/bills/${bill.return_against}`}
              className="font-medium underline hover:no-underline"
            >
              View Original Bill
            </Link>
          </div>
        )}

        {/* ── T04: the store-credit code this credit note issued ─────── */}
        {bill.return_against && bill.redemption_code && (
          <div
            className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl px-5 py-4 text-white shadow-md animate-fade-in"
            data-testid="credit-note-code"
          >
            <p className="text-xs font-medium uppercase tracking-wider text-white/80">Store credit code</p>
            <p className="text-2xl font-bold font-mono tracking-widest">{bill.redemption_code}</p>
            <p className="text-xs text-white/80 mt-1">
              Printed with a QR code on the credit note. Scan it or type it in at payment to redeem.
            </p>
          </div>
        )}

        {/* ── Credit notes issued against this bill ──────────────────── */}
        {/* T03: bills with return records show them in the tracking card below. */}
        {(bill.credit_notes?.length ?? 0) > 0 && (bill.returns?.length ?? 0) === 0 && (
          <div className="bg-warning-50 dark:bg-warning-950 border border-warning-300 dark:border-warning-700 rounded-lg px-4 py-3 text-sm text-warning-800 dark:text-warning-200">
            <p className="font-medium mb-1">Returns against this bill</p>
            <ul className="space-y-0.5">
              {bill.credit_notes!.map((cn) => (
                <li key={cn.id}>
                  Credit Note{' '}
                  <Link to={`/pos/bills/${cn.id}`} className="font-medium underline hover:no-underline">
                    {cn.bill_number}
                  </Link>{' '}
                  — {formatCurrency(Math.abs(Number(cn.grand_total) + Number(cn.rounding_adjustment ?? 0)), currency)}
                  {cn.return_mode ? ` (${cn.return_mode.replace('_', ' ').toLowerCase()})` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── T03: return tracking (RINV number + status) ───────────── */}
        <ReturnTrackingCard
          returns={bill.returns ?? []}
          currency={currency}
          timeZone={getLocationTimezone(bill.location_id)}
          canCancel={canCancelReturn}
          onCancel={handleCancelReturn}
          cancellingId={cancellingReturnId}
        />

        {/* ── Customer section ───────────────────────────────────────── */}
        {(bill.customer_name || bill.customer_phone) && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
              Customer
            </h2>
            <div className="text-sm text-neutral-900 dark:text-neutral-100">
              {bill.customer_name && <span className="font-medium">{bill.customer_name}</span>}
              {bill.customer_phone && (
                <span className="ml-3 text-neutral-500 dark:text-neutral-400">{bill.customer_phone}</span>
              )}
            </div>
          </div>
        )}

        {/* ── Items table ────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider px-4 pt-4 pb-2">
            Items ({bill.items.length})
          </h2>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider border-b border-neutral-200 dark:border-neutral-700">
                  <th className="px-4 py-2">Product</th>
                  <th className="px-4 py-2">SKU</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Unit Price</th>
                  {!noGst && <th className="px-4 py-2 text-right">{tax.taxName} %</th>}
                  <th className="px-4 py-2 text-right">Discount</th>
                  <th className="px-4 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {bill.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-neutral-900 dark:text-neutral-100 font-medium">
                      {item.product_name}
                    </td>
                    <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 text-xs">
                      {item.product_sku}
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-700 dark:text-neutral-300">
                      {item.quantity}
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-700 dark:text-neutral-300">
                      {formatCurrency(item.unit_price, currency)}
                    </td>
                    {!noGst && (
                      <td className="px-4 py-3 text-right text-neutral-500 dark:text-neutral-400">
                        {item.gst_rate}%
                        {Number(item.line_tax) > 0 && (
                          <span className="block text-xs tabular-nums">{formatCurrency(item.line_tax, currency)}</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right text-neutral-500 dark:text-neutral-400">
                      {item.line_discount > 0 ? formatCurrency(item.line_discount, currency) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-neutral-900 dark:text-neutral-100">
                      {formatCurrency(item.line_total, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile items */}
          <div className="md:hidden divide-y divide-neutral-100 dark:divide-neutral-700">
            {bill.items.map((item) => (
              <div key={item.id} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
                    {item.product_name}
                  </span>
                  <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
                    {formatCurrency(item.line_total, currency)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  <span>{item.product_sku}</span>
                  <span>Qty: {item.quantity}</span>
                  <span>{formatCurrency(item.unit_price, currency)} ea</span>
                  {!noGst && <span>{tax.taxName} {item.gst_rate}%</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Summary section ────────────────────────────────────────── */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
          <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
            Summary
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-neutral-600 dark:text-neutral-400">Subtotal</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{formatCurrency(bill.subtotal, currency)}</dd>
            </div>
            {bill.discount_total > 0 && (
              <div className="flex justify-between">
                <dt className="text-neutral-600 dark:text-neutral-400">Discount</dt>
                <dd className="text-danger-600 dark:text-danger-400">−{formatCurrency(bill.discount_total, currency)}</dd>
              </div>
            )}
            {noGst ? (
              <div className="flex justify-between">
                <dt className="text-neutral-600 dark:text-neutral-400">{NOT_GST_REGISTERED}</dt>
                <dd className="text-neutral-500 dark:text-neutral-400">No GST charged</dd>
              </div>
            ) : (
              <div className="flex justify-between">
                <dt className="text-neutral-600 dark:text-neutral-400">Tax</dt>
                <dd className="text-neutral-900 dark:text-neutral-100">{formatCurrency(bill.tax_total, currency)}</dd>
              </div>
            )}
            {(bill.tax_lines ?? []).map((t) => (
              <div key={`${t.kind}-${t.ratePct}`} className="flex justify-between pl-3 text-xs">
                <dt className="text-neutral-500 dark:text-neutral-400">{t.kind} @{t.ratePct}%</dt>
                <dd className="text-neutral-700 dark:text-neutral-300 tabular-nums">{formatCurrency(t.amount, currency)}</dd>
              </div>
            ))}
            {Number(bill.rounding_adjustment ?? 0) !== 0 && (
              <div className="flex justify-between">
                <dt className="text-neutral-600 dark:text-neutral-400">Rounding</dt>
                <dd className="text-neutral-900 dark:text-neutral-100 tabular-nums">
                  {Number(bill.rounding_adjustment) > 0 ? '+' : '−'}
                  {formatCurrency(Math.abs(Number(bill.rounding_adjustment)), currency)}
                </dd>
              </div>
            )}
            <div className="flex justify-between border-t border-neutral-200 dark:border-neutral-700 pt-2 font-semibold">
              <dt className="text-neutral-900 dark:text-neutral-100">Grand Total</dt>
              {/* What the bill settled for: grand_total + the cash rounding. */}
              <dd className="text-neutral-900 dark:text-neutral-100">
                {formatCurrency(Number(bill.grand_total) + Number(bill.rounding_adjustment ?? 0), currency)}
              </dd>
            </div>
            {bill.status === 'COMPLETED' && (
              <>
                <div className="flex justify-between">
                  <dt className="text-neutral-600 dark:text-neutral-400">
                    Amount Paid
                    {bill.payment_method && !isSplit && (
                      <span className="ml-1 capitalize">
                        ({bill.payment_method.replace('_', ' ').toLowerCase()})
                      </span>
                    )}
                  </dt>
                  <dd className="text-neutral-900 dark:text-neutral-100">{formatCurrency(bill.amount_paid, currency)}</dd>
                </div>
                {bill.change_due > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-neutral-600 dark:text-neutral-400">Change Due</dt>
                    <dd className="text-neutral-900 dark:text-neutral-100">{formatCurrency(bill.change_due, currency)}</dd>
                  </div>
                )}
              </>
            )}
          </dl>
        </div>

        {/* ── Payments section (split) ───────────────────────────────── */}
        {isSplit && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
              Payment Breakdown
            </h2>
            <div className="space-y-2">
              {bill.payments.map((payment) => (
                <div key={payment.id} className="flex justify-between text-sm">
                  <span className="text-neutral-700 dark:text-neutral-300">
                    <Badge variant="info" size="sm">{payment.payment_method}</Badge>
                  </span>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">
                    {formatCurrency(payment.amount, currency)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Void reason (QA 4.4.12) ─────────────────────────────────── */}
        {bill.status === 'VOID' && bill.void_reason && (
          <div className="bg-danger-50 dark:bg-danger-900/20 rounded-lg border border-danger-200 dark:border-danger-800 p-4">
            <h2 className="text-xs font-medium text-danger-700 dark:text-danger-300 uppercase tracking-wider mb-2">
              Void reason
            </h2>
            <p className="text-sm text-neutral-700 dark:text-neutral-300">{bill.void_reason}</p>
          </div>
        )}

        {/* ── Notes ──────────────────────────────────────────────────── */}
        {bill.notes && (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <h2 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
              Notes
            </h2>
            <p className="text-sm text-neutral-700 dark:text-neutral-300">{bill.notes}</p>
          </div>
        )}
      </div>

      {/* ═══ Print Success Banner ═══ */}
      {printSuccess && (
        <div className="px-6 pt-1 shrink-0">
          <Alert variant="success" description={printSuccess} dismissible onDismiss={() => setPrintSuccess(null)} />
        </div>
      )}

      {/* ═══ Action Bar ═══ */}
      <div className="shrink-0 flex flex-wrap items-center gap-2 px-6 py-3 bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
        {/* Printer status indicator */}
        {printerType !== 'browser' && (
          <div className={`w-2 h-2 rounded-full shrink-0 ${
            printerStatus === 'connected' ? 'bg-success-500' : printerStatus === 'error' ? 'bg-danger-500' : 'bg-neutral-300 dark:bg-neutral-600'
          }`} title={`Printer: ${printerStatus}`} />
        )}

        {/* Print Receipt — thermal or PDF fallback */}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleThermalPrint}
          disabled={isDownloading || isPrinting}
          loading={isPrinting}
          title="Print receipt (P)"
        >
          <PrinterIcon />
          <span className="ml-1.5">Print</span>
        </Button>

        {/* Duplicate / PDF receipt dropdown */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowReceiptMenu(!showReceiptMenu)}
            disabled={isDownloading || isPrinting}
            loading={isDownloading && !showReceiptMenu}
          >
            <DownloadIcon />
            <span className="ml-1.5 hidden sm:inline">More</span>
          </Button>
          {showReceiptMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowReceiptMenu(false)} />
              <div className="absolute bottom-full left-0 mb-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-20 py-1 min-w-[160px]">
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
                  onClick={handleDuplicatePrint}
                >
                  Print Duplicate
                </button>
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
                  onClick={() => handlePrintReceipt('80mm')}
                >
                  Download 80mm PDF
                </button>
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
                  onClick={() => handlePrintReceipt('58mm')}
                >
                  Download 58mm PDF
                </button>
              </div>
            </>
          )}
        </div>

        {/* COMPLETED actions */}
        {bill.status === 'COMPLETED' && (() => {
          const windowDays = bill.return_window_days ?? 7;
          const daysElapsed = Math.floor(
            (Date.now() - new Date(bill.created_at).getTime()) / (1000 * 60 * 60 * 24),
          );
          const returnWindowExpired = windowDays > 0 && daysElapsed >= windowDays;
          const expiredHint = returnWindowExpired
            ? `Return window of ${windowDays} days has expired (bill is ${daysElapsed} days old)`
            : undefined;
          // T02: units on credit notes or held by a pending request can't be returned again.
          const nothingReturnable = !bill.items.some((i) => returnableQty(i) > 0);
          const awaitingApproval = bill.items.some((i) => (i.pending_return_qty ?? 0) > 0);
          const returnHint = expiredHint
            ?? (nothingReturnable
              ? (awaitingApproval ? 'Awaiting manager approval for the remaining items' : 'All items have been returned')
              : undefined);
          return (
            <>
              <Button variant="secondary" size="sm" onClick={handleDownloadInvoice} disabled={isDownloading} loading={isDownloading}>
                <DownloadIcon />
                <span className="ml-1.5">Invoice</span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label="Share bill"
                onClick={() => {
                  setShareChannel('WHATSAPP');
                  setShareRecipient(bill.customer_phone ?? '');
                  setShowShare(true);
                }}
              >
                <span aria-hidden>↗</span>
                <span className="ml-1.5 hidden sm:inline">Share</span>
              </Button>
              {canIssueCreditNote && (
                // A disabled Button is pointer-events-none, so the hover tooltip
                // comes from this wrapper; the button keeps the title for AT.
                <span className="inline-flex items-center gap-2" title={returnHint}>
                  {awaitingApproval && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
                      Awaiting approval
                    </span>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={openReturnModal}
                    disabled={returnWindowExpired || nothingReturnable}
                    title={returnHint}
                  >
                    <RotateCcwIcon />
                    <span className="ml-1.5">Process Return</span>
                  </Button>
                </span>
              )}
            </>
          );
        })()}

        {/* RETURNED actions */}
        {bill.status === 'RETURNED' && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownloadCreditNote}
              disabled={isDownloading || !creditNoteId}
              loading={isDownloading}
            >
              <DownloadIcon />
              <span className="ml-1.5">Credit Note</span>
            </Button>
            {/* T04: a credit note is shared by WhatsApp / email like an invoice. */}
            {bill.return_against && (
              <Button
                variant="secondary"
                size="sm"
                aria-label="Share credit note"
                onClick={() => {
                  setShareChannel('WHATSAPP');
                  setShareRecipient(bill.customer_phone ?? '');
                  setShowShare(true);
                }}
              >
                <span aria-hidden>↗</span>
                <span className="ml-1.5 hidden sm:inline">Share</span>
              </Button>
            )}
          </>
        )}

        {/* DRAFT actions */}
        {bill.status === 'DRAFT' && (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/pos/billing?resume=${bill.id}`)}
            >
              Resume Bill
            </Button>
            <Button variant="danger" size="sm" onClick={() => setShowVoidConfirm(true)}>
              Void Bill
            </Button>
          </>
        )}
      </div>

      <ManagerApprovalDialog
        open={returnApprovalReason !== null}
        action="RETURN_OVER_THRESHOLD"
        reason={returnApprovalReason ?? ''}
        subjectId={bill.id}
        locationId={bill.location_id ?? undefined}
        onApproved={(grant) => submitReturn({ approval_token: grant.approval_token })}
        onCancel={() => setReturnApprovalReason(null)}
        onAskRemotely={() => submitReturn({})}
      />

      {/* ═══ Return Modal ═══ */}
      <Modal
        open={showShare}
        onClose={() => setShowShare(false)}
        title="Share Bill"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowShare(false)} disabled={isSharing}>Cancel</Button>
            <Button
              variant="primary"
              loading={isSharing}
              disabled={!shareRecipient.trim()}
              onClick={async () => {
                setIsSharing(true);
                try {
                  await billingService.sendInvoice(bill.id, shareChannel, shareRecipient.trim());
                  setShowShare(false);
                  setSuccessMsg(`${bill.return_against ? 'Credit note' : 'Invoice'} queued for ${shareChannel === 'WHATSAPP' ? 'WhatsApp' : 'email'} to ${shareRecipient.trim()}`);
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : 'Could not send the invoice.');
                } finally {
                  setIsSharing(false);
                }
              }}
            >
              Send
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Select
            label="Send via"
            options={[
              { value: 'WHATSAPP', label: 'WhatsApp' },
              { value: 'EMAIL', label: 'Email' },
            ]}
            value={shareChannel}
            onChange={(v) => {
              const next = (typeof v === 'string' ? v : v[0]) === 'EMAIL' ? 'EMAIL' : 'WHATSAPP';
              setShareChannel(next);
              setShareRecipient(next === 'WHATSAPP' ? bill.customer_phone ?? '' : '');
            }}
          />
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {shareChannel === 'WHATSAPP' ? 'Phone number' : 'Email address'}
            <input
              type={shareChannel === 'WHATSAPP' ? 'tel' : 'email'}
              value={shareRecipient}
              onChange={(e) => setShareRecipient(e.target.value)}
              placeholder={shareChannel === 'WHATSAPP' ? '+919876543210' : 'customer@example.com'}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>
        </div>
      </Modal>

      <Modal
        open={showReturn}
        onClose={() => setShowReturn(false)}
        title="Process Return"
        variant="wide"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowReturn(false)} disabled={isProcessingReturn}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleProcessReturn}
              loading={isProcessingReturn}
              disabled={selectedReturnItems.size === 0}
            >
              Confirm Return
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Item selection */}
          <div>
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Select items to return
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {bill.items.map((item) => {
                const free = returnableQty(item);
                const pending = Number(item.pending_return_qty ?? 0);
                const locked = free === 0;
                const qty = returnQuantities[item.id] ?? free;
                const toggle = () => {
                  if (!locked) toggleReturnItem(item.id, free);
                };
                return (
                <div
                  key={item.id}
                  data-testid="return-line-item"
                  role="button"
                  tabIndex={locked ? -1 : 0}
                  aria-disabled={locked || undefined}
                  onClick={toggle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle();
                    }
                  }}
                  className={`flex items-center gap-3 p-2 rounded transition-all duration-200 ${
                    locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800'
                  }`}
                >
                  <span className="pointer-events-none">
                    <Checkbox
                      checked={selectedReturnItems.has(item.id)}
                      onChange={toggle}
                      disabled={locked}
                    />
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {item.product_name}
                    </span>
                    <span className="ml-2 text-xs text-neutral-500">
                      x{item.quantity} &middot; {formatCurrency(item.line_total, currency)}
                    </span>
                    {pending > 0 && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
                        {locked ? 'Awaiting approval' : `${pending} awaiting approval`}
                      </span>
                    )}
                    {locked && pending === 0 && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300">
                        Returned
                      </span>
                    )}
                  </div>

                  {/* Quantity stepper - only meaningful once the line is selected
                      and only shown when more than one unit can still be returned. */}
                  {selectedReturnItems.has(item.id) && free > 1 && (
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Decrease return quantity for ${item.product_name}`}
                        disabled={qty <= 1}
                        onClick={() => setReturnQuantity(item.id, qty - 1, free)}
                      >
                        &minus;
                      </Button>
                      <span className="w-10 text-center text-sm font-medium tabular-nums">
                        {qty}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Increase return quantity for ${item.product_name}`}
                        disabled={qty >= free}
                        onClick={() => setReturnQuantity(item.id, qty + 1, free)}
                      >
                        +
                      </Button>
                      <span className="text-xs text-neutral-500 whitespace-nowrap">of {free}</span>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Return mode */}
          <Select
            label="Return Mode"
            options={RETURN_MODE_OPTIONS}
            value={returnMode}
            onChange={(v) => setReturnMode(typeof v === 'string' ? v : v[0] ?? 'CASH')}
            required
          />

          {/* Reason */}
          <Select
            label="Reason"
            options={RETURN_REASON_OPTIONS}
            value={returnReason}
            onChange={(v) => setReturnReason(typeof v === 'string' ? v : v[0] ?? 'DEFECTIVE')}
            required
          />

          {/* Notes */}
          <TextArea
            label="Additional Notes"
            value={returnNotes}
            onChange={setReturnNotes}
            placeholder="Optional details about the return..."
            rows={2}
          />
        </div>
      </Modal>

      {/* ═══ Void Confirm Dialog ═══ */}
      <ConfirmDialog
        open={showVoidConfirm}
        title="Void Bill"
        description={`Are you sure you want to void ${isDraftBillNumber(bill.bill_number) ? 'this draft bill' : `bill ${bill.bill_number}`}? This action cannot be undone.`}
        confirmLabel="Void Bill"
        variant="danger"
        onConfirm={handleVoidBill}
        onCancel={() => setShowVoidConfirm(false)}
        loading={isVoiding}
      >
        <input
          type="text"
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
          maxLength={500}
          aria-label="Void reason"
          placeholder="Reason (optional)"
          className="mt-3 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-danger-500"
        />
      </ConfirmDialog>
    </div>
  );
}
